import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Users,
  Clock,
  Eye,
  MapPin,
  TrendingUp,
  RefreshCw,
  User,
  Wallet,
  Calendar,
  Heart,
  Radio,
  Wifi,
  WifiOff,
  Activity,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';
import { format, subDays } from 'date-fns';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const AGE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AudienceAnalyticsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [dwellTime, setDwellTime] = useState(null);
  const [selectedScreen, setSelectedScreen] = useState('all');
  const [screenDemographics, setScreenDemographics] = useState(null);
  const [screens, setScreens] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });
  
  // Real-time state
  const [wsConnected, setWsConnected] = useState(false);
  const [liveEvents, setLiveEvents] = useState([]);
  const [liveViewerCount, setLiveViewerCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // WebSocket connection for audience events
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/audience`);
    
    ws.onopen = () => {
      setWsConnected(true);
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'audience_event') {
        const audienceEvent = data.data;
        setLiveEvents(prev => [audienceEvent, ...prev].slice(0, 20));
        setLiveViewerCount(prev => prev + (audienceEvent.viewer_count || 0));
      }
    };
    
    ws.onclose = () => {
      setWsConnected(false);
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
    };
    
    ws.onerror = () => {
      setWsConnected(false);
    };
    
    wsRef.current = ws;
  }, []);

  useEffect(() => {
    connectWebSocket();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [connectWebSocket]);

  useEffect(() => {
    loadScreens();
  }, []);

  useEffect(() => {
    loadData();
  }, [dateRange, selectedScreen]);

  const loadScreens = async () => {
    try {
      const response = await api.get('/screens');
      setScreens(response.data);
    } catch (error) {
      console.error('Failed to load screens');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {
        start_date: dateRange.start,
        end_date: dateRange.end,
      };
      if (selectedScreen !== 'all') {
        params.screen_id = selectedScreen;
      }
      
      const [analyticsRes, dwellRes] = await Promise.all([
        api.get('/audience/analytics', { params }),
        api.get('/audience/dwell-time', { params: selectedScreen !== 'all' ? { screen_id: selectedScreen } : {} }),
      ]);
      setAnalytics(analyticsRes.data);
      setDwellTime(dwellRes.data);
      
      // Load demographics for selected screen
      if (selectedScreen !== 'all') {
        const demoRes = await api.get(`/audience/demographics/${selectedScreen}`);
        setScreenDemographics(demoRes.data);
      } else {
        setScreenDemographics(null);
      }
    } catch (error) {
      toast.error('Failed to load audience analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const ageChartData = analytics?.demographics?.age_groups?.map((a, i) => ({
    name: a.group,
    value: a.percentage,
    fill: AGE_COLORS[i % AGE_COLORS.length],
  })) || [];

  const genderChartData = analytics?.demographics?.gender?.map((g, i) => ({
    name: g.gender,
    value: g.percentage,
    fill: COLORS[i % COLORS.length],
  })) || [];

  const incomeChartData = analytics?.demographics?.income?.map((inc, i) => ({
    name: inc.bracket,
    value: inc.percentage,
    fill: COLORS[i + 2 % COLORS.length],
  })) || [];

  return (
    <div className="space-y-6" data-testid="audience-analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Audience Analytics
          </h1>
          <p className="text-zinc-400 mt-1">
            Demographics, dwell time, and audience insights
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge 
            variant="outline" 
            className={wsConnected ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}
          >
            {wsConnected ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
            {wsConnected ? 'Live' : 'Offline'}
          </Badge>
          <Button variant="outline" onClick={loadData} className="border-zinc-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Live Viewer Count (if simulator running and events coming in) */}
      {liveEvents.length > 0 && (
        <div className="glass-card border-purple-500/30 bg-purple-500/5 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-purple-400 animate-pulse" />
              <span className="text-purple-400 text-sm font-medium">Live Audience Feed</span>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-white font-bold text-lg">{liveViewerCount.toLocaleString()}</p>
                <p className="text-zinc-500 text-xs">Session Viewers</p>
              </div>
              <div className="text-center">
                <p className="text-white font-bold text-lg">{liveEvents.length}</p>
                <p className="text-zinc-500 text-xs">Detections</p>
              </div>
              <div className="text-center">
                <p className="text-emerald-400 font-bold text-lg">
                  {liveEvents.length > 0 ? (liveEvents.reduce((acc, e) => acc + (e.avg_dwell_time_seconds || 0), 0) / liveEvents.length).toFixed(1) : 0}s
                </p>
                <p className="text-zinc-500 text-xs">Avg Dwell</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="glass-card border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <Label className="text-zinc-400 text-xs">Screen</Label>
              <Select value={selectedScreen} onValueChange={setSelectedScreen}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white mt-1">
                  <SelectValue placeholder="All Screens" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Screens</SelectItem>
                  {screens.map((screen) => (
                    <SelectItem key={screen.id} value={screen.id}>{screen.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-zinc-400 text-xs">Start Date</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-white mt-1"
              />
            </div>
            <div className="flex-1">
              <Label className="text-zinc-400 text-xs">End Date</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-white mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Eye className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {(analytics?.summary?.total_estimated_impressions || 0).toLocaleString()}
                </p>
                <p className="text-sm text-zinc-400">Total Impressions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {(analytics?.summary?.total_unique_viewers || 0).toLocaleString()}
                </p>
                <p className="text-sm text-zinc-400">Unique Viewers</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {analytics?.summary?.avg_dwell_time_seconds || 0}s
                </p>
                <p className="text-sm text-zinc-400">Avg Dwell Time</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <MapPin className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {analytics?.summary?.screens_analyzed || 0}
                </p>
                <p className="text-sm text-zinc-400">Screens Analyzed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Demographics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Age Groups */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-400" />
              Age Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={ageChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}%`}
                    labelLine={false}
                  >
                    {ageChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#18181b',
                      border: '1px solid #27272a',
                      borderRadius: '8px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-2 mt-4 justify-center">
              {ageChartData.map((item, index) => (
                <Badge key={index} variant="outline" style={{ borderColor: item.fill, color: item.fill }}>
                  {item.name}: {item.value}%
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Gender */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <User className="w-5 h-5 text-purple-400" />
              Gender Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={genderChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {genderChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#18181b',
                      border: '1px solid #27272a',
                      borderRadius: '8px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4">
              {genderChartData.map((item, index) => (
                <div key={index} className="text-center">
                  <p className="text-2xl font-bold" style={{ color: item.fill }}>{item.value}%</p>
                  <p className="text-zinc-400 text-sm">{item.name}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Income */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-400" />
              Income Brackets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={incomeChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis type="number" stroke="#71717a" fontSize={12} />
                  <YAxis type="category" dataKey="name" stroke="#71717a" fontSize={10} width={80} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#18181b',
                      border: '1px solid #27272a',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hourly Audience Distribution */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white">Hourly Audience Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics?.hourly_distribution || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis
                  dataKey="hour_label"
                  stroke="#71717a"
                  fontSize={10}
                />
                <YAxis stroke="#71717a" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#18181b',
                    border: '1px solid #27272a',
                    borderRadius: '8px',
                  }}
                  formatter={(value, name) => [value.toLocaleString(), name === 'estimated_impressions' ? 'Est. Impressions' : name]}
                />
                <Bar dataKey="estimated_impressions" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Dwell Time Analysis */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white">Dwell Time by Screen</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500">Screen</TableHead>
                <TableHead className="text-zinc-500">Venue Type</TableHead>
                <TableHead className="text-zinc-500 text-right">Avg Dwell</TableHead>
                <TableHead className="text-zinc-500 text-right">Median</TableHead>
                <TableHead className="text-zinc-500 text-right">Engagement</TableHead>
                <TableHead className="text-zinc-500">Optimal Duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dwellTime?.screens?.map((screen, index) => (
                <TableRow key={index} className="border-zinc-800">
                  <TableCell className="text-white font-medium">{screen.screen_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700">
                      {screen.venue_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-zinc-400 text-right">
                    {screen.avg_dwell_time_seconds}s
                  </TableCell>
                  <TableCell className="text-zinc-400 text-right">
                    {screen.median_dwell_time_seconds}s
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline" className={
                      screen.engagement_score > 50
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                    }>
                      {screen.engagement_score}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-blue-400">{screen.optimal_content_duration}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Screen Demographics Detail (when specific screen selected) */}
      {screenDemographics && (
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-400" />
              Audience Interests - {screenDemographics.screen_name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Interests */}
              <div>
                <h4 className="text-zinc-400 text-sm mb-4">Top Interests</h4>
                <div className="space-y-3">
                  {screenDemographics.interests?.map((interest, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-white">{interest.interest}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-zinc-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${Math.min(interest.affinity_score, 100)}%` }}
                          />
                        </div>
                        <span className="text-zinc-400 text-sm w-8">{interest.affinity_score}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Time Patterns */}
              <div>
                <h4 className="text-zinc-400 text-sm mb-4">Traffic by Time Period</h4>
                <div className="space-y-3">
                  {screenDemographics.time_patterns?.map((pattern, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-white text-sm">{pattern.period}</span>
                      <Badge variant="outline" className={
                        pattern.traffic_index >= 120
                          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                          : pattern.traffic_index >= 100
                          ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                          : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                      }>
                        {pattern.traffic_index} index
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
